/**
 * ChatPage — AI 聊天页面
 *
 * 模块架构：
 * ┌──────────────────────────────────────────────────────┐
 * │  ChatPage（编排层）                                    │
 * │  ├─ hooks/                                            │
 * │  │   ├─ useChatSessions    — 会话 CRUD + 持久化        │
 * │  │   ├─ useChatMessages    — 消息管理 + 发送           │
 * │  │   └─ useChatStream      — SSE 流式网络层            │
 * │  ├─ components/                                       │
 * │  │   ├─ ChatSidebar        — 侧边栏（会话列表）        │
 * │  │   ├─ ChatWelcome        — 欢迎页 + 快捷建议         │
 * │  │   ├─ MessageBubble      — 消息气泡 + 思考面板      │
 * │  │   └─ Icons              — SVG 图标集合             │
 * │  ├─ utils/                                           │
 * │  │   └─ streamParser       — SSE 解析工具             │
 * │  └─ types                  — 本地类型定义              │
 * └──────────────────────────────────────────────────────┘
 *
 * 数据流：
 *   用户输入 → ChatInput → handleSend
 *       │
 *       ├─ 无活跃会话 → useChatSessions.createSession()
 *       │                    └─ 设置 activeSessionId
 *       │
 *       ├─ useChatMessages.sendMessageStream()
 *       │       └─ chatAPI.sendMessageStream() → SSE 流
 *       │              └─ readSSEStream() → onChunk 回调
 *       │                     └─ 更新 messages 状态
 *       │
 *       └─ MessageBubble 根据 messages 渲染
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChatInput } from 'starry-sky-ui';
import ChatSidebar from './components/ChatSidebar';
import ChatWelcome from './components/ChatWelcome';
import MessageBubble from './components/MessageBubble';
import { MenuIcon } from './components/Icons';
import { useChatSessions } from './hooks/useChatSessions';
import { useChatMessages } from './hooks/useChatMessages';
import './index.scss';

/** 快捷建议文本列表 */
const SUGGESTIONS = [
  '用 TypeScript 写一个防抖函数',
  'React 和 Vue 的区别',
  '什么是闭包？',
];

/**
 * AI 聊天页面主组件
 * 作为编排层，协调各子组件和 hooks 的数据流转
 */
const ChatPage: React.FC = () => {
  // ==================== 状态管理 ====================

  /** 会话管理 Hook */
  const {
    sessions,
    activeSessionId,
    setActiveSessionId,
    loading: sessionsLoading,
    createSession,
    deleteSession,
    refresh: refreshSessions,
  } = useChatSessions();

  /** 消息管理 Hook */
  const {
    messages,
    sendStatus,
    loadMessages,
    sendMessageStream,
    resetMessages,
  } = useChatMessages();

  /** 移动端侧边栏展开状态 */
  const [sidebarOpen, setSidebarOpen] = useState(false);

  /**
   * 各消息思考面板的展开状态
   * 以 message.id 为 key，value 表示是否展开
   */
  const [expandedThinking, setExpandedThinking] = useState<
    Record<string, boolean>
  >({});

  /** AbortController 引用，用于中断流式请求 */
  const abortRef = useRef<AbortController | null>(null);

  /** 消息列表容器引用，用于自动滚动 */
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const isStreaming = sendStatus === 'sending' || sendStatus === 'streaming';

  // ==================== 副作用 ====================

  /**
   * 切换活跃会话时加载消息列表
   */
  useEffect(() => {
    if (activeSessionId !== null) {
      loadMessages(activeSessionId);
    } else {
      resetMessages();
    }
  }, [activeSessionId, loadMessages, resetMessages]);

  /**
   * 新消息到达时自动滚动到底部
   */
  useEffect(() => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, [messages]);

  // ==================== 事件处理 ====================

  /**
   * 新建对话
   * 仅清除活跃会话进入空白欢迎页，不调用 API。
   * 等用户首次发送消息时再自动创建会话。
   */
  const handleNewChat = useCallback(() => {
    setActiveSessionId(null);
    resetMessages();
    setSidebarOpen(false);
  }, [setActiveSessionId, resetMessages]);

  /**
   * 选择某个会话
   * @param sessionId 会话 ID
   */
  const handleSelectSession = useCallback(
    (sessionId: number) => {
      setActiveSessionId(sessionId);
    },
    [setActiveSessionId]
  );

  /**
   * 删除会话
   * @param sessionId 会话 ID
   */
  const handleDeleteSession = useCallback(
    async (sessionId: number) => {
      await deleteSession(sessionId);
    },
    [deleteSession]
  );

  /**
   * 发送消息（核心流程）
   *
   * 1. 如果没有活跃会话（新对话状态），创建会话
   * 2. 调用 sendMessageStream 发起流式请求（使用临时 sessionId）
   * 3. 流式完成后，将 sessionId 同步到活跃会话和侧边栏
   *    —— 避免 setActiveSessionId 触发 loadMessages 覆盖正在流式写入的消息
   *
   * @param content 用户输入的消息文本
   */
  const handleSend = useCallback(
    async (content: string) => {
      // 步骤1：确保有活跃会话
      let currentSessionId = activeSessionId;

      if (currentSessionId === null) {
        const title = content.slice(0, 10);
        const newId = await createSession(title);
        if (newId === null) return;
        currentSessionId = newId;
        // 先不同步到 activeSessionId，避免 useEffect → loadMessages 覆盖流式消息
        refreshSessions();
      }

      // 步骤2：创建 AbortController
      const controller = new AbortController();
      abortRef.current = controller;

      // 步骤3：发起流式请求（使用本地保留的 sessionId）
      const isNewSession = activeSessionId === null;
      await sendMessageStream(
        currentSessionId,
        content,
        () => {},
        (tempAiId) => {
          setExpandedThinking((prev) => ({ ...prev, [tempAiId]: true }));
        },
        controller.signal
      );

      // 步骤4：流式完成后，如果是新创建的会话才同步选中状态
      // 此时服务端已保存消息，loadMessages 能拿到完整数据
      if (isNewSession) {
        setActiveSessionId(currentSessionId);
      }

      abortRef.current = null;
    },
    [activeSessionId, createSession, sendMessageStream, setActiveSessionId, refreshSessions]
  );

  /**
   * 停止生成（中断流式请求）
   */
  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  /**
   * 切换某条消息的思考面板展开状态
   * @param msgId 消息 ID
   */
  const toggleThinking = useCallback((msgId: string) => {
    setExpandedThinking((prev) => ({
      ...prev,
      [msgId]: !prev[msgId],
    }));
  }, []);

  // ==================== 渲染 ====================

  return (
    <div className="chat-page">
      {/* ===== 左侧：会话列表侧边栏 ===== */}
      <ChatSidebar
        sessions={sessions}
        activeSessionId={activeSessionId}
        open={sidebarOpen}
        loading={sessionsLoading}
        onNewChat={handleNewChat}
        onSelectSession={handleSelectSession}
        onDeleteSession={handleDeleteSession}
        onClose={() => setSidebarOpen(false)}
      />

      {/* ===== 右侧：主聊天区域 ===== */}
      <div className="chat-main">
        {/* ---- 顶部栏 ---- */}
        <div className="chat-topbar">
          <button
            className="chat-menu-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="打开侧边栏"
          >
            <MenuIcon />
          </button>
          <span className="chat-topbar-title">
            {sessions.find((s) => s.id === activeSessionId)?.title || 'AI 助手'}
          </span>
        </div>

        {/* ---- 消息列表 ---- */}
        <div className="chat-messages">
          {messages.length === 0 ? (
            // 无消息：显示欢迎页
            <ChatWelcome
              suggestions={SUGGESTIONS}
              onSend={handleSend}
              disabled={isStreaming}
            />
          ) : (
            // 有消息：渲染消息气泡列表
            messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                thinkingExpanded={!!expandedThinking[msg.id]}
                onToggleThinking={() => toggleThinking(msg.id)}
              />
            ))
          )}
          {/* 滚动锚点 */}
          <div ref={messagesEndRef} />
        </div>

        {/* ---- 输入区域 ---- */}
        <div className="chat-input-area">
          <ChatInput
            onSend={isStreaming ? handleStop : handleSend}
            loading={isStreaming}
            placeholder={
              isStreaming
                ? 'AI 正在回复...'
                : '输入消息，Enter 发送，Shift+Enter 换行'
            }
          />
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
